import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Money, Th, Td, TableWrap, Empty } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";

export default function Subscription() {
  const { api, t, company, refreshCompany, can, download } = useApp();
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState("month");
  const [invoices, setInvoices] = useState([]);
  const load = useCallback(() => api.get("/subscription/invoices").then((r) => setInvoices(r.data)), [api]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data.filter((p) => p.code !== "trial"))); load(); }, [api, load]);
  const sub = company.subscription || {};
  const rw = can("subscription", true);
  const pay = async (plan_code, method) => {
    try {
      const { data } = await api.post("/subscription/checkout", { plan_code, period, method });
      toast.success(data.status === "paid" ? `Paiement accepté (SIMULÉ) · ${data.invoice.number}` : `Virement attendu · réf. ${data.pending.reference}`);
      await refreshCompany();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={company.name} title={t("subscription")} />
      <Panel testid="sub-current">
        <div className="grid gap-4 sm:grid-cols-4 text-sm">
          <div><span className="eyebrow block">{t("plan")}</span><b data-testid="sub-plan-name">{company.plan?.name}</b></div>
          <div><span className="eyebrow block">{t("status")}</span><b data-testid="sub-state">{t(company.sub_state)}</b></div>
          <div><span className="eyebrow block">{t("expires")}</span>{dmy(sub.expires_at)}</div>
          <div><span className="eyebrow block">{t("invoices_sent")}</span><span className="num">{sub.invoices_sent || 0}</span></div>
        </div>
        {sub.pending && <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="sub-pending">Virement en attente : {sub.pending.plan_code} · <Money v={sub.pending.amount} /> HT · réf. <b className="num">{sub.pending.reference}</b> — activation par l'éditeur à réception.</div>}
      </Panel>
      <div className="flex gap-2">
        {["month", "year"].map((p) => <Button key={p} variant={period === p ? "default" : "outline"} size="sm" onClick={() => setPeriod(p)} data-testid={`sub-period-${p}`}>{p === "month" ? t("monthly") : t("yearly")}</Button>)}
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {plans.map((p) => (
          <div key={p.code} className={`rounded-2xl border bg-white p-6 shadow-sm ${p.code === "pro" ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`} data-testid={`plan-card-${p.code}`}>
            <div className="flex items-baseline justify-between"><h3 className="text-xl font-bold">{p.name}</h3>{sub.plan_code === p.code && sub.status === "active" && <span className="text-xs font-semibold text-teal-700">● actuel</span>}</div>
            <div className="mt-3 text-3xl font-extrabold"><Money v={period === "year" ? p.price_year : p.price_month} /> <span className="text-sm font-normal text-slate-500">HT {period === "year" ? t("yearly_price") : t("monthly_price")}</span></div>
            <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
              <li className="flex gap-2"><Check className="h-4 w-4 text-teal-600" />Facturation électronique TEIF + gestion commerciale</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-teal-600" />{p.max_users} {t("users")} · {p.max_docs_month} {t("docs_month")}</li>
              <li className={`flex gap-2 ${p.stock ? "" : "opacity-40 line-through"}`}><Check className="h-4 w-4 text-teal-600" />{t("stock")}</li>
              <li className={`flex gap-2 ${p.accounting ? "" : "opacity-40 line-through"}`}><Check className="h-4 w-4 text-teal-600" />{t("accounting")}</li>
            </ul>
            {rw && (
              <div className="mt-6 flex flex-wrap gap-2">
                <Button onClick={() => pay(p.code, "online")} data-testid={`plan-pay-online-${p.code}`}>{t("pay_online")} <span className="ms-1 text-[10px] opacity-70">({t("simulated")})</span></Button>
                <Button variant="outline" onClick={() => pay(p.code, "transfer")} data-testid={`plan-pay-transfer-${p.code}`}>{t("pay_transfer")}</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <Panel title={t("editor_invoices")}>
        {invoices.length === 0 ? <Empty /> : (
          <TableWrap>
            <thead className="border-b"><tr><Th>{t("number")}</Th><Th>{t("date")}</Th><Th>{t("plan")}</Th><Th className="text-end">{t("total_ttc")}</Th><Th /></tr></thead>
            <tbody>{invoices.map((i) => (
              <tr key={i.id} className="border-b border-slate-50" data-testid={`sub-invoice-${i.number}`}><Td className="num text-xs">{i.number}</Td><Td className="num text-xs">{dmy(i.date)}</Td><Td>{i.plan_code} · {i.period}</Td><Td className="text-end"><Money v={i.totals.ttc} /></Td>
                <Td className="text-end"><Button size="sm" variant="ghost" onClick={() => download(`/subscription/invoices/${i.id}/pdf`)} data-testid={`sub-invoice-pdf-${i.number}`}><FileDown className="h-4 w-4" /></Button></Td></tr>))}</tbody>
          </TableWrap>
        )}
      </Panel>
    </>
  );
}
